// src/dev-error-bridge.tsx
import React from 'react';
import { isChunkLoadError, reloadAfterChunkLoadError } from './utils/chunkLoadRecovery';

const ChunkLoadRecovery = () => (
  <main
    role="alert"
    aria-live="assertive"
    style={{
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      justifyContent: 'center',
      textAlign: 'center',
    }}
  >
    <div>
      <div>
        <h1
          style={{
            borderRight: '1px solid rgba(0, 0, 0, 0.3)',
            display: 'inline-block',
            fontSize: '24px',
            fontWeight: 500,
            letterSpacing: 'normal',
            lineHeight: '49px',
            margin: '0 20px 0 0',
            padding: '0 23px 0 0',
            verticalAlign: 'top',
          }}
        >
          Update
        </h1>
        <div style={{ display: 'inline-block' }}>
          <h2
            style={{
              fontSize: '14px',
              fontWeight: 400,
              letterSpacing: 'normal',
              lineHeight: '49px',
              margin: 0,
            }}
          >
            A new version is available. Reload to continue.
          </h2>
        </div>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: '#fc5c64',
          border: 0,
          borderRadius: '0.5rem',
          color: '#ffffff',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          marginTop: '16px',
          minWidth: '120px',
          padding: '10px 20px',
        }}
      >
        Reload page
      </button>
    </div>
  </main>
);

// --------------------
// Runtime error logging
// --------------------
if (import.meta.env.DEV) {
  const viteHmrSocket = (
    window as Window & {
      __vite_hmr_ws__?: { send: (data: string) => void };
    }
  ).__vite_hmr_ws__;

  const sendToTerminal = (type: 'error' | 'warn', ...args: unknown[]) => {
    viteHmrSocket?.send(JSON.stringify({ type: 'custom', event: type, data: args }));
  };

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    if (args.length > 0) {
      originalConsoleError('❌ [DEV ERROR]', ...args);
    } else {
      originalConsoleError('❌ [DEV ERROR]');
    }
    sendToTerminal('error', ...args);
  };

  const originalConsoleWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (args.length > 0) {
      originalConsoleWarn('⚠️ [DEV WARN]', ...args);
    } else {
      originalConsoleWarn('⚠️ [DEV WARN]');
    }
    sendToTerminal('warn', ...args);
  };

  window.onerror = (message, source, lineno, colno, error) => {
    originalConsoleError(`❌ [DEV ERROR] ${String(message)} at ${source}:${lineno}:${colno}`, error);
    sendToTerminal('error', message, source, lineno, colno, error);
    return false; // let Vite overlay still show
  };

  window.addEventListener('unhandledrejection', (event) => {
    originalConsoleError('❌ [DEV ERROR] Unhandled Promise Rejection:', event.reason);
    sendToTerminal('error', event.reason);
  });
}

// --------------------
// React component error boundary
// --------------------
export class DevErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  state = { hasError: false, error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isChunkLoadError(error) && reloadAfterChunkLoadError()) {
      return;
    }

    console.error('❌ [DEV ERROR][React Component]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const isStaleBuild = isChunkLoadError(this.state.error);

      if (isStaleBuild) {
        return <ChunkLoadRecovery />;
      }

      return (
        <div style={{ color: 'red', padding: '1rem' }}>
          <strong>Something went wrong (check console)</strong>
          <pre>
            {this.state.error ? ((this.state.error as any)?.message ?? String(this.state.error)) : 'Unknown error'}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
